import React, { useEffect, useState } from "react";
import { useLocation, Link, useParams } from "react-router-dom";
import { OPERATIONS_ROUTES } from "../../routes/routes.config";
import PropTypes from "prop-types";
import "../../assets/scss/views/Operations.scss";
import { useLayout } from "../../context/LayoutContext";
import LoadingAnimation from "../../components/loading/LoadingAnimation";

// Importación de vistas (las crearemos después)
import DashboardOperations from "./views_operations/DashboardOperations";
import ViewEcopetrol from "./views_operations/ViewEcopetrol";
import ViewGranTierra from "./views_operations/ViewGranTierra";
import ViewEmerald from "./views_operations/ViewEmerald";
import ViewTerpelNorte from "./views_operations/ViewTerpelNorte";
import ViewTerpelSur from "./views_operations/ViewTerpelSur";
import SettingOperations from "./views_operations/SettingOperations";

export default function Operations({ routeConfig }) {
  const location = useLocation();
  const { id } = useParams();
  const { setHeaderTitle, isHeaderVisible, theme, setIsHeaderVisible } = useLayout();
  const [lastScrollY, setLastScrollY] = useState(0);

  // -------------------------------------------------------------
  // Titulo del header
  // -------------------------------------------------------------
  const getHeaderTitle = () => {
    if (!routeConfig) {
      // Coincidencia exacta
      let currentRoute = Object.values(OPERATIONS_ROUTES).find(
        (route) => route.path === location.pathname
      );

      // Coincidencia dinámica
      if (!currentRoute) {
        currentRoute = Object.values(OPERATIONS_ROUTES).find((route) => {
          const dynamicPathID = route.path.replace(/:id/, id || "");
          return location.pathname === dynamicPathID;
        });
      }

      return currentRoute?.title || "Operaciones";
    }
    return routeConfig.title;
  };
  
  const headerTitle = getHeaderTitle();
  
  // -------------------------------------------------------------
  // Breadcrumbs
  // -------------------------------------------------------------
  const getNavigationParts = () => {
    // Ruta principal
    if (location.pathname === OPERATIONS_ROUTES.main.path) {
      return ["Operaciones"];
    }

    // Buscar en todas las rutas
    for (const key in OPERATIONS_ROUTES) {
      if (location.pathname === OPERATIONS_ROUTES[key].path) {
        return OPERATIONS_ROUTES[key].subtitle.split("/");
      }
    }

    // Fallback
    const parts = location.pathname.split("/");
    return ["Operaciones", parts[parts.length - 1]];
  };

  // -------------------------------------------------------------
  // Ruta anterior
  // -------------------------------------------------------------
  const getPreviousPath = () => {
    // Si no es la ruta principal, volvemos siempre a operations
    if (location.pathname !== OPERATIONS_ROUTES.main.path) {
      return OPERATIONS_ROUTES.main.path;
    }

    // Fallback
    return "/";
  };

  // -------------------------------------------------------------
  // Contenido específico según la ruta
  // -------------------------------------------------------------
  const getRouteSpecificContent = () => {
    switch (location.pathname) {
      case OPERATIONS_ROUTES.ecopetrol.path:
        return <ViewEcopetrol />;
      case OPERATIONS_ROUTES.granTierra.path:
        return <ViewGranTierra />;
      case OPERATIONS_ROUTES.emerald.path:
        return <ViewEmerald />;
      case OPERATIONS_ROUTES.terpelNorte.path:
        return <ViewTerpelNorte />;
      case OPERATIONS_ROUTES.terpelSur.path:
        return <ViewTerpelSur />;
      case OPERATIONS_ROUTES.setting.path:
        return <SettingOperations />;
      default:
        return <DashboardOperations />;
    }
  };

  // Efectos para manejar el título y el scroll
  useEffect(() => {
    setHeaderTitle(headerTitle);
  }, [headerTitle, setHeaderTitle]);
  
  useEffect(() => {
    const handleScroll = (event) => {
      const container = event.target;
      const currentScrollY = container.scrollTop;
      const isScrollingUp = currentScrollY < lastScrollY;

      // Constantes para los umbrales
      const SCROLL_THRESHOLD = 100;
      const SHOW_HEADER_THRESHOLD = 50;

      if (!isScrollingUp && currentScrollY > SCROLL_THRESHOLD) {
        setIsHeaderVisible(false);
      } else if (currentScrollY <= SHOW_HEADER_THRESHOLD) {
        setIsHeaderVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    // Optimización con debounce
    const debouncedHandleScroll = debounce(handleScroll, 16);

    // Importante: Actualizamos el selector para trabajar con cualquier tema
    const contentContainers = document.querySelectorAll(
      '[class*="-operations-content"]'
    );

    contentContainers.forEach((container) => {
      container.addEventListener("scroll", debouncedHandleScroll, {
        passive: true,
      });
    });

    return () => {
      contentContainers.forEach((container) => {
        container.removeEventListener("scroll", debouncedHandleScroll);
      });
    };
  }, [lastScrollY, setIsHeaderVisible]);
  
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // -------------------------------------------------------------
  // Render principal
  // -------------------------------------------------------------
  const navigationParts = getNavigationParts();
  const isMainRoute = location.pathname === OPERATIONS_ROUTES.main.path;
  
  return (
    <div className={`GM__${theme}-operations-container`}>
      <header
        className={`GM__${theme}-operations-header ${
          !isHeaderVisible ? "header-hidden" : ""
        }`}
      >
        <h1 className={`GM__${theme}-operations-title`}>{getHeaderTitle()}</h1>

        <nav className={`GM__${theme}-operations-navigation`}>
          {isMainRoute ? (
            <span className={`GM__${theme}-navigation-item-current`}>
              {navigationParts[0]}
            </span>
          ) : (
            <>
              {navigationParts.map((part, index) => (
                <React.Fragment key={`${index}-${part}`}>
                  {index > 0 && (
                    <span className={`GM__${theme}-navigation-separator`}>
                      /
                    </span>
                  )}
                  {index === navigationParts.length - 1 ? (
                    <span className={`GM__${theme}-navigation-item-current`}>
                      {part}
                    </span>
                  ) : (
                    <Link
                      to={
                        index === 0
                          ? OPERATIONS_ROUTES.main.path
                          : getPreviousPath()
                      }
                      className={`GM__${theme}-navigation-item-clickable`}
                    >
                      {part}
                    </Link>
                  )}
                </React.Fragment>
              ))}
            </>
          )}
        </nav>

        <div className={`GM__${theme}-header-divider`} />
      </header>

      <main
        className={`GM__${theme}-operations-content ${
          !isHeaderVisible ? "header-hidden" : ""
        }`}
      >
        {getRouteSpecificContent()}
      </main>
    </div>
  );
}

Operations.propTypes = {
  routeConfig: PropTypes.shape({
    path: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
  }),
};